import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../services/apiService";
import {
    clearAuthSession,
    getAuthSession,
    isAuthSessionExpired,
    persistAuthSession,
} from "../utils/authSession";

const AuthContext = createContext();
const SYSTEM_CODE = "vv";

const normalizeRole = (roleName) => {
    if (!roleName || typeof roleName !== "string") return "";
    return roleName.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_");
};

const extractRoles = (permissoes) => {
    const papeisPorSistema = permissoes?.papeis || [];
    const sistema = papeisPorSistema.find((p) => p.codigo_sistema === SYSTEM_CODE);
    if (!sistema || !Array.isArray(sistema.papeis)) return [];

    const roles = sistema.papeis
        .map((p) => normalizeRole(p.nome_papel))
        .filter(Boolean);

    return Array.from(new Set(roles));
};

const mapSessionState = (session) => {
    const roles = extractRoles(session?.permissoes);
    const user = session?.user ? { ...session.user, roles } : null;

    return { user, roles };
};

export const AuthProvider = ({ children }) => {
    const initialSession = getAuthSession();
    const initialState = isAuthSessionExpired(initialSession)
        ? { user: null, roles: [] }
        : mapSessionState(initialSession);

    const [user, setUser] = useState(initialState.user);
    const [roles, setRoles] = useState(initialState.roles);
    const [loading, setLoading] = useState(false);

    const applySession = (data) => {
        const session = persistAuthSession(data);
        const nextState = mapSessionState(session);
        setUser(nextState.user);
        setRoles(nextState.roles);
        return session;
    };

    const clearSession = () => {
        clearAuthSession();
        setUser(null);
        setRoles([]);
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // Mesmo que falhe, limpa localmente
        } finally {
            clearSession();
        }
    };

    const login = async (email, password) => {
        setLoading(true);
        try {
            const res = await api.post("/auth/login", { email, password, sistema: SYSTEM_CODE });
            const systemCodes = Array.isArray(res?.permissoes?.sistemas)
                ? res.permissoes.sistemas.map((sistema) => sistema.codigo_sistema).filter(Boolean)
                : [];

            if (!systemCodes.includes(SYSTEM_CODE)) {
                throw new Error("Seu usuário não possui acesso ao Voto Visão.");
            }

            applySession(res);
            return { success: true };
        } catch (err) {
            throw new Error(err.message || "Erro ao autenticar.");
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        try {
            const session = getAuthSession();
            if (!session?.access_token) return;

            const res = await api.post("/auth/refresh", { token: session.access_token });
            applySession(res);
        } catch {
            logout();
        }
    };

    useEffect(() => {
        const session = getAuthSession();

        if (isAuthSessionExpired(session)) {
            clearSession();
            return;
        }

        if (!session?.permissoes) {
            api.get("/auth/me")
                .then((res) => applySession({
                    ...session,
                    ...res,
                    access_token: session.access_token,
                    expires_at: session.expires_at,
                }))
                .catch(() => logout());
        }

        const timeToRefresh = (Number(session.expires_at) - Date.now() / 1000 - 60) * 1000;
        if (timeToRefresh > 0) {
            const timer = setTimeout(() => refresh(), timeToRefresh);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider value={{ user, roles, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
